import { useState, useCallback } from "react";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import Colors from "../../../assets/Variables/ColorsVariables";
import adminMethods from "../../../services/admin";
import SearchBoxComponent from "../../Generals/SearchBox/SearchBoxComponent";
import Button3px from "../../Generals/Buttons/3pxButton";
import TableComponent from "../../Generals/Tables/TableComponent";
import NotificationModal from "../../Generals/Notifications/NotificationModal";
import TableToolTip from "../../Generals/Tooltips/TableToolTip.js";
import IconComponent from "../../Generals/Icons/IconComponent.js";
import * as Styles from "../Accounts/Styles.js";

const HistoricalScreen = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [userRole, setUserRole] = useState("");

  const [page, setPage] = useState(0);
  const [totalRegisters, setTotalRegisters] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const [modalConfig, setModalConfig] = useState({
    open: false,
    iconType: "info",
    title: "",
    message: "",
    onAccept: () => setModalConfig(prev => ({ ...prev, open: false })),
  });

  const fetchHistoricalData = useCallback(async (targetPage) => {
    setLoading(true);
    console.log("Fetching historical data with searchTerm:", searchTerm, "and page:", targetPage);
    try {
      const params = {
        login: !isNaN(searchTerm) && searchTerm.trim() !== "" ? searchTerm : null,
        search: isNaN(searchTerm) ? searchTerm : null,
        page: targetPage,
        quantity: 10,
      };

      const res = await adminMethods.getHistoricalSettings(params);

      if (res.data && res.data.status === "Success") {
        const responseData = res.data.data;

        setUserRole(responseData.role);
        setTotalRegisters(responseData.total_registers);
        setPageCount(responseData.total_pages);
        setRows(responseData.data.map((item, index) => ({
          id: `hist-${targetPage}-${index}`,
          ...item
        })));
      }
    } catch (error) {
      console.error("Error fetching historical data:", error);
      setModalConfig({
        open: true,
        iconType: "error",
        title: "Error",
        message: "Error obtaining data. Please try again.",
        onAccept: () => setModalConfig(prev => ({ ...prev, open: false })),
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const handleSearchClick = () => {
    if (!searchTerm) return;
    setPage(0);
    fetchHistoricalData(0);
  };

  const columns = userRole === "Signal" ? [
    { field: "time", headerName: "Time", flex: 1 },
    { field: "strategy_name", headerName: "Strategy Name", flex: 1.5 },
    { field: "fee_value", headerName: "Fee Value", flex: 1 },
    { field: "drawdown_mode", headerName: "DD Mode", flex: 1 },
    { field: "max_drowdown", headerName: "Max. Drawdown", flex: 1 },
    { field: "message", headerName: "Message", flex: 2 },
  ] : [
    { field: "time", headerName: "Time", flex: 1 },
    { field: "strategy_followed", headerName: "Strategy Followed", flex: 1.5 },
    { field: "copy_mode", headerName: "Copy Mode", flex: 1 },
    { field: "risk_ratio", headerName: "Risk Ratio", flex: 1 },
    {
      field: "max_drowdown",
      headerName: "Max. Drawdown",
      flex: 1,
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Max. Drawdown
          <TableToolTip title="The max value of drawdown to reach the limit">
            <IconComponent icon="help" size="12px" />
          </TableToolTip>
        </Box>
      )
    },
    { field: "message", headerName: "Message", flex: 2 },
  ];

  return (
    <Paper elevation={2} sx={Styles.containerPaper}>
      <NotificationModal {...modalConfig} />

      <Typography variant="h5" gutterBottom>
        Historical Settings
      </Typography>

      <Typography sx={{ fontSize: "15px", color: "#687B8E", mb: 2 }}>
        Check here for historical records of signal and follower configurations.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: "#F8FAFC",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: 2.5,
              color: Colors.Secondary_700,
              marginTop: "-10px",
            }}
          >
            Search
          </Typography>
          <SearchBoxComponent
            value={searchTerm}
            handleSearchTerm={(e) => setSearchTerm(e.target.value)}
            placeholderprops={"Search for login, name or strategy"}
            variant="statements"
          />
        </Box>

        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "flex-end",
          ml: "left"
        }}>
          <Button3px
            label={"Search"}
            color={Colors.Warning_50}
            backgroundColor={Colors.Error_500}
            HandleOnClick={handleSearchClick}
            height="40px"
          />
        </Box>
      </Box>

      <Box style={{ width: "100%", marginTop: "0px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box sx={{ width: "100%", overflowX: 'auto' }}>
            <Box sx={{ minWidth: "1400px" }}>
              <TableComponent
                rows={rows}
                columns={columns}
                totalAmount={totalRegisters}
                pageCount={pageCount}
                page={page}
                handleChangePage={(newPage) => {
                  setPage(newPage);
                  fetchHistoricalData(newPage);
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default HistoricalScreen;